import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import BackgroundEffects from '../../../components/layout/BackgroundEffects/BackgroundEffects'
import Header from '../../../components/layout/Header/Header'
import Card from '../../../components/common/Card/Card'
import Button from '../../../components/common/Button/Button'
import Badge from '../../../components/common/Badge/Badge'
import { getCurrentUser, logout, getUserData } from '../../../services/authService'
import { getAllEmployees } from '../../../services/timeOffService'
import {
  getAttendanceHistory,
  getAllAttendanceRecords,
  getAttendanceByDateRange,
  exportAttendanceToCSV,
  formatCheckInTime,
  calculateNumericHours
} from '../../../services/attendanceService'

const Attendance = () => {
  const navigate = useNavigate()

  // Auth & User State
  const [user, setUser] = useState(null)
  const [userData, setUserData] = useState(null)
  const [isAdmin, setIsAdmin] = useState(false)

  // Attendance Records State
  const [attendanceRecords, setAttendanceRecords] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  // Filters State
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [selectedEmployee, setSelectedEmployee] = useState('all')
  const [employees, setEmployees] = useState([])

  // Export State
  const [isExporting, setIsExporting] = useState(false)

  const todayString = new Date().toISOString().split('T')[0]

  // Load user and initial data
  useEffect(() => {
    const currentUser = getCurrentUser()
    if (!currentUser) {
      navigate('/login')
      return
    }

    setUser(currentUser)

    // Get user data
    getUserData(currentUser.uid).then((result) => {
      if (result.success) {
        setUserData(result.data)

        const adminStatus = result.data.role === 'super_admin' || result.data.role === 'admin'
        setIsAdmin(adminStatus)

        // Set default date range (30 days ago to today)
        const today = new Date()
        const thirtyDaysAgo = new Date()
        thirtyDaysAgo.setDate(today.getDate() - 30)

        setEndDate(today.toISOString().split('T')[0])
        setStartDate(thirtyDaysAgo.toISOString().split('T')[0])

        // Load initial data
        loadAttendanceData(result.data, adminStatus, thirtyDaysAgo.toISOString().split('T')[0], today.toISOString().split('T')[0])

        if (adminStatus) {
          loadEmployees()
        }
      }
    })
  }, [navigate])

  // Load employees list for filter (admin only)
  const loadEmployees = async () => {
    const result = await getAllEmployees()
    if (result.success) {
      setEmployees(result.data)
    }
  }

  // Load attendance data based on role and filters
  const loadAttendanceData = async (userDataParam = userData, isAdminParam = isAdmin, start = startDate, end = endDate) => {
    if (!userDataParam) return

    setLoading(true)
    setError('')

    try {
      let result

      if (isAdminParam) {
        // Super admin: use date range filtering
        if (start && end) {
          const employeeFilter = selectedEmployee !== 'all' ? selectedEmployee : null
          result = await getAttendanceByDateRange(start, end, employeeFilter)
        } else {
          result = await getAllAttendanceRecords()
        }
      } else {
        // Employee: own records only
        if (start && end) {
          result = await getAttendanceByDateRange(start, end, userDataParam.employeeId)
        } else {
          result = await getAttendanceHistory(userDataParam.employeeId, 100)
        }
      }

      if (result.success) {
        let data = Array.isArray(result.data) ? result.data : []

        if (!isAdminParam && userDataParam) {
          const fallbackName = userDataParam.fullName || userDataParam.email || userDataParam.employeeId
          data = data.map(record => ({
            ...record,
            employeeName: (record.employeeName && record.employeeName !== 'Unknown')
              ? record.employeeName
              : fallbackName
          }))
        }

        setAttendanceRecords(data)
      } else {
        setError(result.error || 'Failed to load attendance records')
      }
    } catch (error) {
      console.error('Load error:', error)
      setError('An error occurred while loading attendance records')
    } finally {
      setLoading(false)
    }
  }

  const formatDateLabel = (dateString) => {
    if (!dateString) return ''
    const dateObj = new Date(dateString + 'T00:00:00')
    if (Number.isNaN(dateObj.getTime())) return dateString
    return dateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  }

  const clearError = (message, timeout = 5000) => {
    setError(message)
    setTimeout(() => setError(''), timeout)
  }

  const handleDateFilterChange = () => {
    if (!startDate || !endDate) {
      clearError('Please select both From and To dates')
      return
    }

    if (startDate > todayString || endDate > todayString) {
      clearError('Cannot filter for future dates')
      return
    }

    if (startDate > endDate) {
      const normalizedStart = endDate
      const normalizedEnd = startDate
      setStartDate(normalizedStart)
      setEndDate(normalizedEnd)
      setSuccessMessage(`Dates corrected: From ${formatDateLabel(normalizedStart)} to ${formatDateLabel(normalizedEnd)}`)
      setTimeout(() => setSuccessMessage(''), 5000)
      loadAttendanceData(userData, isAdmin, normalizedStart, normalizedEnd)
      return
    }

    loadAttendanceData()
  }

  // Handle employee filter change
  useEffect(() => {
    if (userData && isAdmin && startDate && endDate) {
      loadAttendanceData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployee])

  // Handle CSV export
  const handleExportCSV = () => {
    if (attendanceRecords.length === 0) {
      setError('No records to export')
      setTimeout(() => setError(''), 3000)
      return
    }

    setIsExporting(true)

    try {
      const result = exportAttendanceToCSV(attendanceRecords)

      if (result.success) {
        setSuccessMessage(`Exported ${result.count} attendance record${result.count === 1 ? '' : 's'} successfully!`)
        setTimeout(() => setSuccessMessage(''), 3000)
      } else {
        setError(result.error || 'Failed to export attendance records')
        setTimeout(() => setError(''), 5000)
      }
    } catch (error) {
      console.error('Export error:', error)
      setError('An error occurred while exporting')
      setTimeout(() => setError(''), 5000)
    } finally {
      setIsExporting(false)
    }
  }

  // Handle logout
  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString + 'T00:00:00')
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  return (
    <div style={{ minHeight: '100vh', position: 'relative' }}>
      <BackgroundEffects />
      <Header user={user} userData={userData} onLogout={handleLogout} />

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem 1rem' }}>
        {/* Page Header */}
        <div style={{ marginBottom: '2rem' }}>
          <Button
            variant="ghost"
            icon="fas fa-arrow-left"
            onClick={() => navigate('/dashboard')}
            style={{ marginBottom: '1rem' }}
          >
            Back to Dashboard
          </Button>
          <h1 style={{
            margin: 0,
            fontSize: 'var(--psyber-font-size-3xl)',
            color: 'var(--psyber-text)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <i className="fas fa-user-clock" style={{ color: 'var(--psyber-primary)' }}></i>
            Attendance Records
          </h1>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div style={{
            padding: '1rem',
            marginBottom: '1rem',
            background: 'var(--psyber-success-bg)',
            border: '1px solid var(--psyber-success)',
            borderRadius: 'var(--psyber-radius-md)',
            color: 'var(--psyber-success)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <i className="fas fa-check-circle"></i>
            {successMessage}
          </div>
        )}

        {error && (
          <div style={{
            padding: '1rem',
            marginBottom: '1rem',
            background: 'var(--psyber-error-bg)',
            border: '1px solid var(--psyber-error)',
            borderRadius: 'var(--psyber-radius-md)',
            color: 'var(--psyber-error)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        )}

        {/* Filters Card */}
        <Card style={{ marginBottom: '2rem' }}>
          <h3 style={{
            margin: '0 0 1rem 0',
            fontSize: 'var(--psyber-font-size-xl)',
            color: 'var(--psyber-text)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <i className="fas fa-filter"></i>
            Filters
          </h3>

          <div style={{
            display: 'flex',
            gap: '1rem',
            flexWrap: 'wrap',
            alignItems: 'flex-end'
          }}>
            {/* Start Date */}
            <div>
              <label
                htmlFor="start-date"
                style={{
                  display: 'block',
                  marginBottom: '0.25rem',
                  fontSize: 'var(--psyber-font-size-xs)',
                  color: 'var(--psyber-text-secondary)',
                  fontWeight: 600
                }}
              >
                From Date
              </label>
              <input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="psyber-input"
                max={todayString}
                style={{ minWidth: '150px' }}
              />
            </div>

            {/* End Date */}
            <div>
              <label
                htmlFor="end-date"
                style={{
                  display: 'block',
                  marginBottom: '0.25rem',
                  fontSize: 'var(--psyber-font-size-xs)',
                  color: 'var(--psyber-text-secondary)',
                  fontWeight: 600
                }}
              >
                To Date
              </label>
              <input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="psyber-input"
                max={todayString}
                style={{ minWidth: '150px' }}
              />
            </div>

            {/* Apply Filter Button */}
            <Button
              variant="primary"
              icon="fas fa-filter"
              onClick={handleDateFilterChange}
            >
              Apply Filter
            </Button>

            {/* Employee Filter (Super Admin Only) */}
            {isAdmin && (
              <div>
                <label
                  htmlFor="employee-filter"
                  style={{
                    display: 'block',
                    marginBottom: '0.25rem',
                    fontSize: 'var(--psyber-font-size-xs)',
                    color: 'var(--psyber-text-secondary)',
                    fontWeight: 600
                  }}
                >
                  Employee
                </label>
                <select
                  id="employee-filter"
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  className="psyber-input"
                  style={{ minWidth: '200px' }}
                >
                  <option value="all">All Employees</option>
                  {employees.map(emp => (
                    <option key={emp.employeeId} value={emp.employeeId}>
                      {emp.fullName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Export Button */}
            <div style={{ marginLeft: 'auto' }}>
              <Button
                variant="secondary"
                icon="fas fa-download"
                onClick={handleExportCSV}
                disabled={isExporting || attendanceRecords.length === 0}
                loading={isExporting}
              >
                {isExporting ? 'Exporting...' : 'Export to CSV'}
              </Button>
            </div>
          </div>
        </Card>

        {/* Attendance Records Grid */}
        {loading ? (
          <Card style={{ textAlign: 'center', padding: '3rem' }}>
            <i
              className="fas fa-spinner fa-spin"
              style={{
                fontSize: '3rem',
                color: 'var(--psyber-primary)',
                marginBottom: '1rem'
              }}
            ></i>
            <p style={{ margin: 0, color: 'var(--psyber-text-secondary)' }}>
              Loading attendance records...
            </p>
          </Card>
        ) : attendanceRecords.length === 0 ? (
          <Card style={{ textAlign: 'center', padding: '3rem' }}>
            <i
              className="fas fa-calendar-alt"
              style={{
                fontSize: '4rem',
                color: 'var(--psyber-text-muted)',
                marginBottom: '1rem'
              }}
            ></i>
            <p style={{ margin: 0, color: 'var(--psyber-text-secondary)', fontSize: 'var(--psyber-font-size-lg)' }}>
              No attendance records found
            </p>
            <p style={{ margin: '0.5rem 0 0 0', color: 'var(--psyber-text-muted)', fontSize: 'var(--psyber-font-size-sm)' }}>
              Try adjusting your filters or date range
            </p>
          </Card>
        ) : (
          <div className="psyber-grid psyber-grid-3" style={{ gap: '1.5rem' }}>
            {attendanceRecords.map((record) => (
              <Card key={record.id}>
                {/* Employee Info */}
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{
                    margin: 0,
                    fontSize: 'var(--psyber-font-size-lg)',
                    color: 'var(--psyber-text)',
                    fontWeight: 600
                  }}>
                    {record.employeeName}
                  </h4>
                  <p style={{
                    margin: '0.25rem 0 0 0',
                    fontSize: 'var(--psyber-font-size-sm)',
                    color: 'var(--psyber-text-secondary)'
                  }}>
                    {record.employeeId}
                  </p>
                </div>

                {/* Date */}
                <div style={{
                  marginBottom: '1rem',
                  padding: '0.5rem',
                  background: 'var(--psyber-card-hover)',
                  borderRadius: 'var(--psyber-radius-sm)',
                  textAlign: 'center'
                }}>
                  <i className="fas fa-calendar-day" style={{ marginRight: '0.5rem', color: 'var(--psyber-primary)' }}></i>
                  <strong>{formatDate(record.date)}</strong>
                </div>

                {/* Check In/Out Times */}
                <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 'var(--psyber-font-size-sm)', color: 'var(--psyber-text-secondary)' }}>
                    <i className="fas fa-sign-in-alt" style={{ marginRight: '0.5rem', color: 'var(--psyber-success)' }}></i>
                    Check In:
                  </span>
                  <span style={{ fontWeight: 600, color: 'var(--psyber-text)' }}>
                    {formatCheckInTime(record.checkIn) || 'N/A'}
                  </span>
                </div>

                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 'var(--psyber-font-size-sm)', color: 'var(--psyber-text-secondary)' }}>
                    <i className="fas fa-sign-out-alt" style={{ marginRight: '0.5rem', color: 'var(--psyber-error)' }}></i>
                    Check Out:
                  </span>
                  <span style={{ fontWeight: 600, color: record.checkOut ? 'var(--psyber-text)' : 'var(--psyber-warning)' }}>
                    {record.checkOut ? formatCheckInTime(record.checkOut) : 'Still working'}
                  </span>
                </div>

                {/* Total Hours */}
                <div style={{
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  background: 'var(--psyber-primary-opacity-10)',
                  borderRadius: 'var(--psyber-radius-md)',
                  border: '1px solid var(--psyber-primary)',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: 'var(--psyber-font-size-xs)', color: 'var(--psyber-text-secondary)', marginBottom: '0.25rem' }}>
                    Total Hours
                  </div>
                  <div style={{
                    fontSize: 'var(--psyber-font-size-2xl)',
                    fontWeight: 700,
                    color: 'var(--psyber-primary)'
                  }}>
                    {record.checkOut ? calculateNumericHours(record.checkIn, record.checkOut).toFixed(2) + 'h' : '0.00h'}
                  </div>
                </div>

                {/* Status Badge */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Badge variant={record.checkOut ? 'success' : 'warning'}>
                    {record.checkOut ? (
                      <>
                        <i className="fas fa-check-circle" style={{ marginRight: '0.5rem' }}></i>
                        Complete
                      </>
                    ) : (
                      <>
                        <i className="fas fa-hourglass-half" style={{ marginRight: '0.5rem' }}></i>
                        In Progress
                      </>
                    )}
                  </Badge>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default Attendance
